import React, { Component, Context } from "react";
import ProjectService from "@app/projects/projectService";
import { FontAwesome, QesIcons } from "@app/shared/icons/qesIcons";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FlyoutContext } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import { toast } from "react-toastify";
import { formatDateTime } from "@app/shared/formatDateTime";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { GapFillStatusEnum } from "@app/projects/network-assessment/adequacy";
import { GapFillStatusDescriptionMap } from "@app/projects/network-assessment/adequacy/gapFillStatusDescriptionMap";
import GapFillStatusHistory from "@app/projects/results/adequacy/gapFillStatusHistory";
import { Timeline } from "@app/shared/timeline/Timeline";
import { TimelineEvent } from "@app/shared/timeline/TimelineEvent";

interface FlyoutContextType {
  handleCloseFlyout: () => void;
}

interface GapFillStatusHistoryProps {
  customProjectId: number;
  projectRunConfigurationId: number;
  regionSetId: number;
  specialtyGroupCode: string;
  clearCacheImpactAnalysisRunConfigurations: () => void;
  gapFillStatusDescriptionMap: GapFillStatusDescriptionMap
}

interface GapFillStatusHistoryState {
  gapFillStatusHistories: GapFillStatusHistory[];
  showModal: boolean;
}

export default class GapFillStatusHistoryTimeline extends Component<GapFillStatusHistoryProps, GapFillStatusHistoryState> {
  private deleteButtonRef = React.createRef<HTMLButtonElement>();
  static contextType: Context<FlyoutContextType> = FlyoutContext;
  declare context: FlyoutContextType;
  private readonly projectService = new ProjectService();

  constructor(props: GapFillStatusHistoryProps) {
    super(props);

    this.state = {
      gapFillStatusHistories: [],
      showModal: false
    };
  }

  public async componentDidMount() {
    this.setState({
      gapFillStatusHistories: await this.projectService.getGapFillStatusHistory(this.props.customProjectId, this.props.projectRunConfigurationId, this.props.regionSetId, this.props.specialtyGroupCode)
    });
  }

  private removeAllGapResolutionStatusHistory = async (): Promise<void> => {
    await this.projectService.deleteGapFillStatusHistoryAsync(this.props.customProjectId, this.props.regionSetId, this.props.specialtyGroupCode);

    this.context.handleCloseFlyout();
    this.props.clearCacheImpactAnalysisRunConfigurations();
    toast("Gap Resolution Status History successfully removed!",
      {
        containerId: "z-index-9999",
        type: "success"
      }
    );
  };

  private removeGapResolutionStatusHistoryItem = async (gapFillStatus: GapFillStatusEnum, gapFillStatusUpdatedDate: string): Promise<void> => {
    await this.projectService.deleteGapFillStatusItemAsync(this.props.customProjectId, this.props.regionSetId, this.props.specialtyGroupCode, gapFillStatus, gapFillStatusUpdatedDate);

    this.context.handleCloseFlyout();
    this.props.clearCacheImpactAnalysisRunConfigurations();
    toast("Gap Resolution Status successfully removed!",
      {
        containerId: "z-index-9999",
        type: "success"
      }
    );
  };

  private onHideModalClick = async (confirmed: boolean) : Promise<void> => {
    if (confirmed) {
      await this.removeAllGapResolutionStatusHistory();
    }

    this.setState({
      showModal: false,
    });
  };

  private showModal = (): void => {
    this.setState({
      showModal: true,
    });
  }

  public render() {
    if (!this.state.gapFillStatusHistories) {
      return null;
    }

    return (
      <>
        <Modal
          isVisible={!!this.state.showModal}
          content={{
            body: "Warning - Deleting the gap resolution status will permanently delete the history.",
            cancelButtonText: "Cancel",
            confirmButtonAction: () => this.onHideModalClick(true),
            confirmButtonText: "Delete",
            confirmButtonType: "primary-danger",
            title: "Warning",
          }}
          closeModal={() => this.onHideModalClick(false)}
          hideBodyScroll={true}
          triggerElement={this.deleteButtonRef}
        />
        <div>
          <div className="d-flex justify-content-end">
            <RoleRestricted requiredRoles={[Roles.QUEST_USER, Roles.GAP_RESOLUTION_STATUS_MANAGER]}>
              <QESButton qesType="secondary-danger" qesRef={this.deleteButtonRef} onClick={() => this.showModal()} qesTestId="gap-fill-status__remove_all">Remove All</QESButton>
            </RoleRestricted>
          </div>
          <Timeline>
            {this.state.gapFillStatusHistories.map((gapFillStatusHistory) => {
              return (
                <TimelineEvent
                  key={`${gapFillStatusHistory.status}:${gapFillStatusHistory.timeStamp}`}
                  title={`Updated by ${gapFillStatusHistory.updatedByName} on`}
                  subtitle={`${formatDateTime(gapFillStatusHistory.timeStamp)}`}
                  icon={<QesIcon iconName={QesIcons.Calendar} isDecorative width="24"/>}
                >
                  <div className="container timeline-container position-relative">
                    <div className="remove_container">
                      <RoleRestricted requiredRoles={[Roles.QUEST_USER, Roles.GAP_RESOLUTION_STATUS_MANAGER]}>
                        <QESButton
                          qesType="secondary-danger"
                          onClick={() => this.removeGapResolutionStatusHistoryItem(gapFillStatusHistory.status, gapFillStatusHistory.timeStamp)}
                          qesTestId="gap-fill-status__remove_item">
                          <QesIcon iconName={FontAwesome.Trash} width="12" height="12" description="Remove" />
                        </QESButton>
                      </RoleRestricted>
                    </div>
                    <div className="row mb5">
                      <div className="col-5 col-lg-4">
                        <strong>Changed To:</strong>
                      </div>
                      <div className="col col-lg">
                        {this.props.gapFillStatusDescriptionMap[gapFillStatusHistory.status]}
                      </div>
                    </div>
                    <div className="row mb5">
                      <div className="col-5 col-lg-4">
                        <strong>Changed From:</strong>
                      </div>
                      <div className="col col-lg">
                        {this.props.gapFillStatusDescriptionMap[gapFillStatusHistory.previousStatus]}
                      </div>
                    </div>
                    <div className="row mb5">
                      <div className="col-5 col-lg-4">
                        <strong>Comments:</strong>
                      </div>
                      <div className="col col-lg">
                        {gapFillStatusHistory.comment ?? "---"}
                      </div>
                    </div>
                  </div>
                </TimelineEvent>
              );
            })}
          </Timeline>
        </div>
      </>
    );
  }

}
